import { isPlatformBrowser } from '@angular/common';
import { ErrorHandler, Injectable, inject, PLATFORM_ID } from '@angular/core';
import { SupabaseService } from '../../../shared/services/supabase.service';
import { toMasked } from '../shared/masked-ean';
import { REPORT_JOB_POLLER } from './report-job-poller';
import {
  type BrandCompareRequest,
  type BrandCompareResult,
  ReportJobRepository,
} from './report-job-repository';

const JOB_TABLE = 'portal_report_jobs';
const JOB_TYPE = 'brand_compare';
const REPORT_BUCKET = 'portal-reports';

// The expand's own cadence and ceiling, unchanged: they belong with the poll, which is here.
const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 60000;

@Injectable()
export class SupabaseReportJobRepository extends ReportJobRepository {
  // Not AuthStore: that import is portal-domain-no-portal-outside-domain.
  private readonly supabase = inject(SupabaseService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly pollJob = inject(REPORT_JOB_POLLER);
  private readonly errorHandler = inject(ErrorHandler);

  async runBrandCompare(request: BrandCompareRequest): Promise<BrandCompareResult> {
    const { data: job, error } = await this.supabase.client
      .from(JOB_TABLE)
      .insert({
        params: {
          job_type: JOB_TYPE,
          // The single encoding on this path: the worker joins `medpdb2`, keyed by the
          // masked form (.claude/rules/portal-domain.md).
          eans: request.eans.map(toMasked),
          vendors: [...request.vendors],
          start: request.start,
          end: request.end,
        },
        status: 'queued',
        created_by: request.requestedBy,
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    // Every null here reads alike to the caller, so a refused insert is reported rather than
    // left as a spinner that never settles (.claude/rules/data-access.md, "react by constraint").
    if (error || !job) {
      this.errorHandler.handleError(
        new Error(
          `[ReportJobRepository] runBrandCompare: ${JOB_TABLE} took no row: ` +
            // `.single()` can answer with neither a row nor an error.
            `${error?.message ?? 'the insert answered with neither a row nor an error'}`
        )
      );
      return null;
    }

    // The request is queued off the browser too; only the wait does not belong there.
    if (!isPlatformBrowser(this.platformId)) return null;

    const finished = await this.pollJob(
      this.supabase.client,
      job.id,
      POLL_INTERVAL_MS,
      POLL_TIMEOUT_MS,
      {
        shouldStop: request.shouldStop,
      }
    );

    if (finished.status === 'failed') {
      throw new Error(
        `${JOB_TYPE} job ${job.id} failed: ${JSON.stringify(finished.error ?? 'unknown')}`
      );
    }

    if (!finished.storage_key) return null;

    const { data: file, error: downloadError } = await this.supabase.client.storage
      .from(REPORT_BUCKET)
      .download(finished.storage_key);
    // The job named a key, so one that will not read back is a defect, not the exit above.
    if (!file) {
      this.errorHandler.handleError(
        new Error(
          `[ReportJobRepository] runBrandCompare: ${JOB_TYPE} job ${job.id} named ` +
            `${REPORT_BUCKET}/${finished.storage_key}, which did not read back: ` +
            `${downloadError?.message ?? 'the download answered with neither a file nor an error'}`
        )
      );
      return null;
    }

    return { document: JSON.parse(await file.text()) as unknown };
  }
}
